import { useEffect } from "react";
import { useSelector } from "react-redux";
// Components
import NoData from "../../components/NoData";
import GraphPlatformSplit from "./GraphPlatformSplit";
import GraphBookingsDev from "./GraphBookingsDev";
import GraphCategorySplit from "./GraphCategorySplit";
// actions
import { useActions } from "../../common/hooks/useActions";
import { employeesActionCreators } from "../../common/store/modules/employeesModule";
import { dashboardActionCreators } from "../../common/store/modules/dashboardModule";

const Dashboard = () => {
  let key = 0;

  // states
  const requestError = useSelector(state => state.global.requestError);
  const noBookingsData = useSelector(state => state.bookings.noBookingsData);
  const dateRangeSelection = useSelector(state => state.home.dateRangeSelection);
  const dashboardData = useSelector(state => state.dashboard.dashboardData);
  const hotelCurrency = useSelector(state => state.home.hotelData?.hotelCurrency);
  const filteredBookings = useSelector(state => state.bookings.filteredBookings);
  const employeesInDashboard = useSelector(state => state.employees.employeesInDashboard);
  const employeeSort = useSelector(state => state.employees.employeeSort);

  // actions
  const setEmployeesInDashboard = useActions(employeesActionCreators?.setEmployeesInDashboardAction, []);
  const sortEmployees = useActions(employeesActionCreators?.sortEmployeesAction, []);
  const setMainDashboardData = useActions(dashboardActionCreators?.setMainDashboardDataAction, []);

  // fill Dashboard data
  useEffect(() => {
    if(filteredBookings){
    handleAllDashboardData(filteredBookings);}
  }, [filteredBookings]);

  const sortEmployeeData = column => {
    const direction = employeeSort?.column ? (employeeSort?.direction === "asc" ? "desc" : "asc") : "desc";
    const sortedData = employeesInDashboard.sort((a, b) => {
      if (column === "name") {
        const nameA = a.name.toUpperCase(); // ignore upper and lowercase
        const nameB = b.name.toUpperCase(); // ignore upper and lowercase
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      } else if (column === "totalBookings") {
        return a.totalBookings - b.totalBookings;
      } else if (column === "totalRevenue") {
        return a.totalRevenue - b.totalRevenue;
      } else if (column === "totalCommission") {
        return a.totalCommission - b.totalCommission;
      }
    });

    if (direction === "desc") {
      sortedData.reverse();
    }

    setEmployeesInDashboard(sortedData);
    sortEmployees({
      column,
      direction
    });
  };

  // Calculate dashboard data
  const fillDashboardData = allBookingsData => {
    let bookings = [];
    for (let booking of allBookingsData) {
      if (booking.status === "booked" || booking.status === "closed") {
        bookings.push(booking);
      }
    }
    let revenue = bookings.map(({ total_price_without_fees }) => total_price_without_fees);
    let commission = bookings
      .map(({ commission_booking }) => commission_booking?.total_hotel_commission)
      .filter(num => typeof num === "number");
    let totalRevenue = revenue.reduce((a, b) => +a + +b, 0);
    let totalCommission = commission.reduce((a, b) => +a + +b, 0);
    setMainDashboardData({
      totalBookings: bookings.length,
      totalRevenue: totalRevenue,
      totalCommission: totalCommission
    });
  };

  // Sales by Employee / Expenses created by Employee / Sales by Offer table on Dashboard
  const loadEmployeeData = allBookingsData => {
    let bookings = [];
    for (let booking of allBookingsData) {
      if (booking.status === "booked" || booking.status === "closed") {
        bookings.push(booking);
      }
    }
    let employeesInDashboard = [];
    let counter = 0;
    let i = 1;
    for (let booking of bookings) {
      const totalHotelCommission = booking?.commission_booking?.total_hotel_commission
        ? parseFloat(booking.commission_booking.total_hotel_commission)
        : 0;
      if (booking.employee != null) {
        let change = false;
        counter++;
        if (employeesInDashboard.length === 0) {
          let newEmployee = {
            id: i,
            name: booking.employee.first_name,
            totalBookings: 1,
            totalRevenue: parseFloat(booking.total_price_without_fees).toFixed(2),
            totalCommission: totalHotelCommission.toFixed(2)
          };
          employeesInDashboard.push(newEmployee);
        }
        Loop1: for (let employee of employeesInDashboard) {
          if (counter === 1) {
            change = true;
            break Loop1;
          }
          if (booking.employee.first_name === employee.name) {
            employee.totalBookings++;
            employee.totalRevenue = (
              parseFloat(employee.totalRevenue) + parseFloat(booking.total_price_without_fees)
            ).toFixed(2);
            employee.totalCommission = (parseFloat(employee.totalCommission) + totalHotelCommission).toFixed(2);
            change = true;
          }
        }

        if (change === false) {
          let newEmployee = {
            id: ++i,
            name: booking.employee.first_name,
            totalBookings: 1,
            totalRevenue: parseFloat(booking.total_price_without_fees).toFixed(2),
            totalCommission: totalHotelCommission.toFixed(2)
          };
          employeesInDashboard.push(newEmployee);
        }

        employeesInDashboard.sort((a, b) => {
          return b.totalCommission - a.totalCommission;
        });
        sortEmployees({ column: "totalCommission", direction: "desc" });
      }
    }

    setEmployeesInDashboard(employeesInDashboard);
  };

  const handleAllDashboardData = bookings => {
    fillDashboardData(bookings);
    loadEmployeeData(bookings);
  };

  return (
    <div className="dashboard component">
      {dashboardData?.totalBookings === 0 && noBookingsData === true ? (
        <NoData message="NoData" />
      ) : requestError === true ? (
        <NoData message="RequestError" />
      ) : null}
      <div>
        <div className="row">
          <div className="col-lg-3">
            <div className="ibox ">
              <div className="ibox-title">
                <h5>Bookings</h5>
              </div>
              <div className="ibox-content">
                <span className="no-margins" data-cy="booking-counter">
                  {dashboardData?.totalBookings}
                </span>
                <small>New bookings</small>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="ibox ">
              <div className="ibox-title">
                <h5>Revenue</h5>
              </div>
              <div className="ibox-content">
                <span className="no-margins">
                  {hotelCurrency} {dashboardData?.totalRevenue?.toFixed(2)}
                </span>
                <small>Revenue</small>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="ibox ">
              <div className="ibox-title">
                <h5>Commission</h5>
              </div>
              <div className="ibox-content">
                <span className="no-margins">
                  {hotelCurrency} {dashboardData?.totalCommission?.toFixed(2)}
                </span>
                <small>Total commission</small>
              </div>
            </div>
          </div>
          <div className="col-lg-3">
            <div className="ibox ">
              <div className="ibox-title">
                <h5>Platform Split</h5>
              </div>
              <div className="ibox-content">
                {filteredBookings?.length > 0 ? <GraphPlatformSplit bookings={filteredBookings} /> : null}
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="ibox ">
              <div className="ibox-title">
                <h5>Development of bookings</h5>
              </div>
              <div className="ibox-content">
                {filteredBookings?.length && (
                  <GraphBookingsDev bookings={filteredBookings} dateRangeSelection={dateRangeSelection} />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <>
            <div className="col-lg-4">
              <div className="ibox ">
                <div className="ibox-title">
                  <h5>Sales by Employee</h5>
                </div>
                <div className="ibox-content">
                  <div className="table-responsive overflow-hid">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th className="employee_table-head" onClick={() => sortEmployeeData("id")}>
                            <span>#</span>
                          </th>
                          <th className="employee_table-head" onClick={() => sortEmployeeData("name")}>
                            <span>User</span>
                          </th>
                          <th className="employee_table-head" onClick={() => sortEmployeeData("totalBookings")}>
                            <span>Bookings</span>
                          </th>
                          <th className="employee_table-head" onClick={() => sortEmployeeData("totalRevenue")}>
                            <span>Revenue</span>
                          </th>
                          <th className="employee_table-head" onClick={() => sortEmployeeData("totalCommission")}>
                            <span>Commission</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {employeesInDashboard &&
                          employeesInDashboard.map((employee, index) => {
                            return (
                              <tr key={key++}>
                                <th>{++index}</th>
                                <th>{employee.name}</th>
                                <th>{employee.totalBookings}</th>
                                <th>
                                  {hotelCurrency}{" "}
                                  {parseFloat(employee.totalRevenue).toLocaleString("de-ch", {
                                    minimumFractionDigits: 2
                                  })}
                                </th>
                                <th>
                                  {hotelCurrency} {employee.totalCommission}
                                </th>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="ibox ">
                <div className="ibox-title">
                  <h5>Revenue by Category</h5>
                </div>
                <div className="ibox-content">
                  {filteredBookings?.length > 0 && (
                    <GraphCategorySplit bookings={filteredBookings} type={"CategoryRevenue"} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="ibox ">
                <div className="ibox-title">
                  <h5>Commission by Category</h5>
                </div>
                <div className="ibox-content">
                  {filteredBookings?.length > 0 && (
                    <GraphCategorySplit bookings={filteredBookings} type={"CategoryCommission"} />
                  )}
                </div>
              </div>
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
